<script setup lang="ts">
import type { ContentSectionStandalone } from '~/models/Content/ContentSectionStandalone'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { gradeTypeNames } from '~/utils/gradeSorter'
import useAuthStore from '~/stores/auth'
import { PendoTrackName } from '~/models/Pendo'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import usePendo from '~/composables/usePendo'
import { useContentHelper } from '~/composables/useContentHelper'
import { useFavoritesApi } from '~/api/favoritesApi'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'

const { section } = defineProps<{ section: ContentSectionStandalone }>()

const { t } = useI18n()
const { findContents } = useContentApi()
const { buildResourceUrl } = useUrlBuilder()
const { pendoTrack, pendoFlushNow } = usePendo()
const { selectedGradeType } = storeToRefs(useAuthStore())
const { isAuthenticated } = storeToRefs(useAuthStore())
const { isFile, isPdf } = useContentHelper()
const { getFavoriteList } = useFavoritesApi()
const router = useRouter()

const { data: content } = useQuery({
  enabled: computed(() => isAuthenticated.value && Number(section.resource?.destinationContentId) > 0),
  queryKey: computed(() => ['content-id', 'dont-invalidate-on-grade-change', section.contentId]),
  queryFn: async () => (await findContents({
    contentIdCriterion: [Number(section.resource!.destinationContentId)],
    mainLocationCriterion: true,
  }, 1))[0],
  staleTime: Infinity,
  retry: 1,
})

const { data: list } = useQuery({
  enabled: computed(() => isAuthenticated.value && !!section.listId),
  queryKey: computed(() => ['favorite-list', section.listId]),
  queryFn: () => getFavoriteList(section.listId!),
  staleTime: Infinity,
  retry: 1,
})

const listRoute = computed(() => list?.value ? router.resolve({ name: 'favorite_list', params: { listId: list.value.id } }).href : '')
const link = computed(() => content?.value ? buildResourceUrl(content.value) : listRoute.value)

const isAnchor = computed(() => !link.value.startsWith('/'))
const transitionEffect = computed(() => !content.value || (!isFile(content.value) && !isPdf(content.value)))

const track = () => {
  if ((!content?.value && !list.value) || !selectedGradeType.value)
    return
  pendoTrack(PendoTrackName.BannerArticle, {
    title: content.value?.title ?? list.value?.name ?? '',
    gradeType: gradeTypeNames[selectedGradeType.value],
    url: link.value,
  })
  pendoFlushNow()
}

const subtitle = computed(() => {
  const subject = section.subjects[0] ? t(`metadata.subjects.${section.subjects[0]}`) : null
  const label = list.value ? t('favorite.list') : null
  return [subject, label].filter(Boolean).join(' | ')
})

const backgroundColor = computed(() => section.colorPair.background)
const color = computed(() => section.colorPair.text)
</script>

<template>
  <LinkOrAnchor
    v-if="link"
    :link
    :is-anchor
    :transition-effect
    class="ring-offset-seagreen-50 hover:ring-seagreen-10 grid size-full items-center overflow-hidden rounded-lg text-center ring-offset-2 transition hover:ring focus-visible:ring md:min-h-64"
    :style="{ backgroundColor }"
    :class="{
      'grid-cols-1': !section.thumbnail,
      'grid-cols-1 sm:grid-cols-2': section.thumbnail,
    }"
    @click="track"
  >
    <div class="flex size-full flex-col justify-center px-3 py-8">
      <p
        class="pb-4 text-xs font-medium tracking-wider uppercase md:text-sm"
        :style="{ color }"
        v-text="subtitle"
      />
      <span
        class="text-seagreen-50 fluid-text-xl !leading-snug font-bold"
        :style="{ color }"
        v-text="section.title"
      />
      <RichTextRenderer
        v-if="section.description"
        class="mt-2 max-w-prose text-base"
        :style="{ color }"
        :text="section.description"
      />
    </div>
    <div
      v-if="section.thumbnail"
      class="aspect-video size-full sm:aspect-[5/3]"
    >
      <img
        class="size-full object-cover"
        :src="section.thumbnail"
        :alt="section.thumbnailAlt"
      >
    </div>
  </LinkOrAnchor>
</template>
